/**
 * TemplateService
 * Service for communication template operations
 * Route: api/template
 */
import { BaseService } from '../base/BaseService';
/**
 * TemplateService
 * Handles communication template CRUD operations
 */
export class TemplateService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? TemplateService.DEFAULT_SERVICE_PATH,
            basePath: TemplateService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== EMAIL TEMPLATES ====================
    /**
     * Get all email templates
     * GET api/template/email
     */
    async getEmailTemplates() {
        const response = await this.getEmailTemplatesRaw();
        return response.data ?? [];
    }
    /**
     * Get email templates with full response
     */
    getEmailTemplatesRaw() {
        const endpoint = this.getEndpoint('email', 'email');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get email template by ID
     * GET api/template/email/{id}
     */
    async getEmailTemplate(id) {
        const response = await this.getEmailTemplateRaw(id);
        if (!response.data) {
            throw new Error(`Email template ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get email template with full response
     */
    getEmailTemplateRaw(id) {
        const endpoint = this.getEndpoint('email-by-id', `email/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Create email template
     * POST api/template/email
     */
    async createEmailTemplate(template) {
        const response = await this.createEmailTemplateRaw(template);
        if (!response.data) {
            throw new Error(response.message || 'Failed to create email template');
        }
        return response.data;
    }
    /**
     * Create email template with full response
     */
    createEmailTemplateRaw(template) {
        const endpoint = this.getEndpoint('email', 'email');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, template);
    }
    /**
     * Update email template
     * PUT api/template/email/{id}
     */
    async updateEmailTemplate(id, template) {
        const response = await this.updateEmailTemplateRaw(id, template);
        if (!response.data) {
            throw new Error(response.message || 'Failed to update email template');
        }
        return response.data;
    }
    /**
     * Update email template with full response
     */
    updateEmailTemplateRaw(id, template) {
        const endpoint = this.getEndpoint('email-update', `email/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, template);
    }
    /**
     * Delete email template
     * DELETE api/template/email/{id}
     */
    async deleteEmailTemplate(id) {
        const response = await this.deleteEmailTemplateRaw(id);
        return response.success;
    }
    /**
     * Delete email template with full response
     */
    deleteEmailTemplateRaw(id) {
        const endpoint = this.getEndpoint('email-delete', `email/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
    // ==================== SMS TEMPLATES ====================
    /**
     * Get all SMS templates
     * GET api/template/sms
     */
    async getSmsTemplates() {
        const response = await this.getSmsTemplatesRaw();
        return response.data ?? [];
    }
    /**
     * Get SMS templates with full response
     */
    getSmsTemplatesRaw() {
        const endpoint = this.getEndpoint('sms', 'sms');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get SMS template by ID
     * GET api/template/sms/{id}
     */
    async getSmsTemplate(id) {
        const response = await this.getSmsTemplateRaw(id);
        if (!response.data) {
            throw new Error(`SMS template ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get SMS template with full response
     */
    getSmsTemplateRaw(id) {
        const endpoint = this.getEndpoint('sms-by-id', `sms/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Create SMS template
     * POST api/template/sms
     */
    async createSmsTemplate(template) {
        const response = await this.createSmsTemplateRaw(template);
        if (!response.data) {
            throw new Error(response.message || 'Failed to create SMS template');
        }
        return response.data;
    }
    /**
     * Create SMS template with full response
     */
    createSmsTemplateRaw(template) {
        const endpoint = this.getEndpoint('sms', 'sms');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, template);
    }
    /**
     * Update SMS template
     * PUT api/template/sms/{id}
     */
    async updateSmsTemplate(id, template) {
        const response = await this.updateSmsTemplateRaw(id, template);
        if (!response.data) {
            throw new Error(response.message || 'Failed to update SMS template');
        }
        return response.data;
    }
    /**
     * Update SMS template with full response
     */
    updateSmsTemplateRaw(id, template) {
        const endpoint = this.getEndpoint('sms-update', `sms/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, template);
    }
    /**
     * Delete SMS template
     * DELETE api/template/sms/{id}
     */
    async deleteSmsTemplate(id) {
        const response = await this.deleteSmsTemplateRaw(id);
        return response.success;
    }
    /**
     * Delete SMS template with full response
     */
    deleteSmsTemplateRaw(id) {
        const endpoint = this.getEndpoint('sms-delete', `sms/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
    // ==================== PUSH TEMPLATES ====================
    /**
     * Get all push notification templates
     * GET api/template/push
     */
    async getPushTemplates() {
        const response = await this.getPushTemplatesRaw();
        return response.data ?? [];
    }
    /**
     * Get push templates with full response
     */
    getPushTemplatesRaw() {
        const endpoint = this.getEndpoint('push', 'push');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get push template by ID
     * GET api/template/push/{id}
     */
    async getPushTemplate(id) {
        const response = await this.getPushTemplateRaw(id);
        if (!response.data) {
            throw new Error(`Push template ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get push template with full response
     */
    getPushTemplateRaw(id) {
        const endpoint = this.getEndpoint('push-by-id', `push/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Create push template
     * POST api/template/push
     */
    async createPushTemplate(template) {
        const response = await this.createPushTemplateRaw(template);
        if (!response.data) {
            throw new Error(response.message || 'Failed to create push template');
        }
        return response.data;
    }
    /**
     * Create push template with full response
     */
    createPushTemplateRaw(template) {
        const endpoint = this.getEndpoint('push', 'push');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, template);
    }
    /**
     * Update push template
     * PUT api/template/push/{id}
     */
    async updatePushTemplate(id, template) {
        const response = await this.updatePushTemplateRaw(id, template);
        if (!response.data) {
            throw new Error(response.message || 'Failed to update push template');
        }
        return response.data;
    }
    /**
     * Update push template with full response
     */
    updatePushTemplateRaw(id, template) {
        const endpoint = this.getEndpoint('push-update', `push/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.put(url, template);
    }
    /**
     * Delete push template
     * DELETE api/template/push/{id}
     */
    async deletePushTemplate(id) {
        const response = await this.deletePushTemplateRaw(id);
        return response.success;
    }
    /**
     * Delete push template with full response
     */
    deletePushTemplateRaw(id) {
        const endpoint = this.getEndpoint('push-delete', `push/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.delete(url);
    }
    // ==================== WHATSAPP TEMPLATES ====================
    /**
     * Get all WhatsApp templates
     * GET api/template/whatsapp
     */
    async getWhatsAppTemplates() {
        const response = await this.getWhatsAppTemplatesRaw();
        return response.data ?? [];
    }
    /**
     * Get WhatsApp templates with full response
     */
    getWhatsAppTemplatesRaw() {
        const endpoint = this.getEndpoint('whatsapp', 'whatsapp');
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    /**
     * Get WhatsApp template by ID
     * GET api/template/whatsapp/{id}
     */
    async getWhatsAppTemplate(id) {
        const response = await this.getWhatsAppTemplateRaw(id);
        if (!response.data) {
            throw new Error(`WhatsApp template ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get WhatsApp template with full response
     */
    getWhatsAppTemplateRaw(id) {
        const endpoint = this.getEndpoint('whatsapp-by-id', `whatsapp/${id}`);
        const url = this.buildUrl(endpoint);
        return this.http.get(url);
    }
    // ==================== CACHE OPERATIONS ====================
    /**
     * Invalidate template cache
     * POST api/template/cache/invalidate
     */
    async invalidateCache() {
        const response = await this.invalidateCacheRaw();
        return response.success;
    }
    /**
     * Invalidate cache with full response
     */
    invalidateCacheRaw() {
        const endpoint = this.getEndpoint('cache-invalidate', 'cache/invalidate');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, {});
    }
}
TemplateService.DEFAULT_SERVICE_PATH = 'communications';
TemplateService.BASE_PATH = 'api/template';
//# sourceMappingURL=TemplateService.js.map